/**
 * Income Wizard UI Controller
 * 
 * Handles the user interface for the Income Wizard modal
 * Manages wizard steps, form interactions, and data visualization
 */

// Default expense items for each category
const DEFAULT_EXPENSE_ITEMS = {
  'Housing': ['Rent/Mortgage Payment', 'Property Tax'],
  'Transportation': ['Car Payment', 'Gas & Fuel'],
  'Food & Dining': ['Groceries', 'Restaurants & Takeout'],
  'Healthcare': ['Health Insurance Premium', 'Prescription Medications'],
  'Emergency Fund': ['Emergency Savings', 'Unexpected Expense Buffer'],
  'Savings': ['General Savings', 'Short-term Goal Fund'],
  'Debt Payments': ['Credit Card Payment', 'Student Loan Payment'],
  'Insurance': ['Auto Insurance', 'Life Insurance'],
  'Basic Insurance': ['Auto Insurance', 'Life Insurance'],
  'Personal Care': ['Haircuts & Grooming', 'Clothing & Accessories'],
  'Entertainment': ['Streaming Services', 'Movies & Events'],
  'Miscellaneous': ['Gifts & Donations', 'Miscellaneous Expenses'],
  'Childcare & Education': ['Daycare/Babysitting', 'School Supplies'],
  'Retirement Savings': ['401(k) Contribution', 'IRA Contribution'],
  'Investment Portfolio': ['Stock Investments', 'Mutual Funds'],
  'Travel & Leisure': ['Vacation Fund', 'Weekend Getaways'],
  'Income Buffer Fund': ['Variable Income Reserve', 'Seasonal Income Buffer'],
  'Business Expenses': ['Business Equipment', 'Professional Development'],
  'Utilities & Services': ['Electricity & Gas', 'Internet & Phone']
};

import { 
  incomeManager, 
  IncomeSource, 
  INCOME_TYPES, 
  INCOME_FREQUENCIES,
  getIncomeTypeDisplayName,
  getFrequencyDisplayName 
} from './incomeManagement.js';
import { formatCurrency } from './utils/currency.js';
import {
  getCurrentBudgetData,
  restoreBudgetData,
  captureBudgetData,
  setDefaultVersion,
  saveBudgetVersion,
  loadBudgetVersion,
  updateVersionsList,
  updateWelcomeScreen,
  parseCurrencyInput
} from './budget.js';
import { getCurrentUser } from './auth.js';
import { 
  BUDGET_TEMPLATES, 
  getTemplate, 
  getTemplateWithHCOL, 
  calculateTemplateAmounts 
} from './budgetTemplates.js';

// Safely encode category names for use as DOM IDs
function encodeCategoryName(categoryName) {
  return categoryName
    .replace(/&/g, 'and')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9\-_]/g, '')
    .toLowerCase();
}

// Math expression evaluator for input fields
function evaluateMathExpression(expression) {
  try {
    // Clean the expression - only allow numbers, basic operators, parentheses, and decimals
    const cleanExpr = expression.replace(/[^0-9+\-*/().\s]/g, '');
    
    // Basic validation - don't eval if it looks suspicious
    if (!cleanExpr || cleanExpr.length === 0) return null;
    if (cleanExpr.includes('++') || cleanExpr.includes('--')) return null;
    if (/[+\-*/]{2,}/.test(cleanExpr)) return null; // Multiple operators in a row
    
    // Evaluate the expression safely
    const result = Function('"use strict"; return (' + cleanExpr + ')')();
    
    // Validate result
    if (typeof result === 'number' && !isNaN(result) && isFinite(result)) {
      return Math.round(result * 100) / 100; // Round to 2 decimal places
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

// Wizard state
let currentStep = 1;
let editingIncomeId = null;
let wizardData = {
  incomeSources: [],
  selectedTemplate: null,
  isHighCostLiving: false,
  finalTemplate: null,
  allocations: {},
  totalIncome: 0
};

// Initialize Income Wizard UI
export function initializeIncomeWizardUI() {
  // Just log that we're ready - we'll set up listeners when modal opens
  console.log('Income Wizard UI initialized - listeners will be set up when modal opens');
}

// Setup event listeners
function setupEventListeners() {
  console.log('Setting up event listeners');
  
  // Add income source button
  const addIncomeBtn = document.getElementById('add-income-source-btn');
  if (addIncomeBtn) {
    addIncomeBtn.removeEventListener('click', () => showIncomeSourceForm()); // Remove existing
    addIncomeBtn.addEventListener('click', () => showIncomeSourceForm(null)); // Explicitly pass null
    console.log('Add income source button event listener attached');
  } else {
    console.warn('Add income source button not found');
  }

  // Save income source button
  const saveIncomeBtn = document.getElementById('save-income-source-btn');
  if (saveIncomeBtn) {
    saveIncomeBtn.removeEventListener('click', saveIncomeSource); // Remove existing
    saveIncomeBtn.addEventListener('click', saveIncomeSource);
    console.log('Save income source button event listener attached');
  } else {
    console.warn('Save income source button not found');
  }

  // Income type change handler
  const incomeGrossNetSelect = document.getElementById('income-gross-net');
  if (incomeGrossNetSelect) {
    incomeGrossNetSelect.removeEventListener('change', toggleTaxWithholding);
    incomeGrossNetSelect.addEventListener('change', toggleTaxWithholding);
  }

  // Frequency change handler
  const frequencySelect = document.getElementById('income-frequency');
  if (frequencySelect) {
    frequencySelect.removeEventListener('change', updateAmountHelpText);
    frequencySelect.addEventListener('change', updateAmountHelpText);
  }
}

// Open Income Wizard
export function openIncomeWizard() {
  const modal = document.getElementById('income-wizard-modal');
  if (!modal) return;

  // Reset wizard state
  currentStep = 1;
  editingIncomeId = null;
  wizardData = {
    incomeSources: [],
    selectedTemplate: null,
    isHighCostLiving: false,
    finalTemplate: null,
    allocations: {},
    totalIncome: 0
  };
  
  // Load existing income sources from income manager
  loadExistingIncomeSources().catch(console.error);

  // Force modal to be positioned at body level to escape container constraints
  if (modal.parentNode !== document.body) {
    document.body.appendChild(modal);
  }

  // Show modal
  modal.style.display = 'flex';
  document.body.style.overflow = 'hidden';

  // Setup event listeners now that modal is visible
  setupEventListeners();

  // Show first step
  showWizardStep(1);
}

// Close Income Wizard
export function closeIncomeWizard() {
  const modal = document.getElementById('income-wizard-modal');
  if (!modal) return;

  modal.style.display = 'none';
  document.body.style.overflow = '';

  // Hide income source form if open
  hideIncomeSourceForm();
}

// Load existing income sources from income manager
async function loadExistingIncomeSources() {
  try {
    console.log('=== DEBUG: Loading existing income sources ===');
    console.log('incomeManager exists:', !!incomeManager);
    
    // First, ensure income manager data is loaded from Firebase
    if (incomeManager) {
      console.log('Loading income manager data from Firebase...');
      await incomeManager.loadData();
      console.log('Income manager data loaded. Sources count:', incomeManager.incomeSources.size);
      console.log('Income manager sources:', incomeManager.incomeSources);
    }
    
    // Also check if there are income items in the main budget view that we should load
    const budgetIncomeItems = document.querySelectorAll('#income-items .input-group');
    console.log('Found budget income items:', budgetIncomeItems.length);
    
    if (incomeManager && incomeManager.incomeSources.size > 0) {
      console.log('Loading existing income sources into wizard:', incomeManager.incomeSources);
      
      // Convert income manager sources to wizard format
      incomeManager.incomeSources.forEach((source, sourceId) => {
        const wizardSource = {
          id: sourceId,
          name: source.name,
          type: source.type,
          frequency: source.frequency,
          expectedAmount: source.expectedAmount,
          isGrossIncome: source.isGrossIncome,
          taxWithholding: source.taxWithholding,
          paymentSchedule: source.paymentSchedule || {}
        };
        
        wizardData.incomeSources.push(wizardSource);
      });
      
      console.log('Loaded income sources into wizard:', wizardData.incomeSources);
      renderIncomeSourcesList();
    } else if (budgetIncomeItems.length > 0) {
      console.log('No income manager sources, but found budget items. Loading from budget view...');
      loadIncomeFromBudgetView();
    } else {
      console.log('No existing income sources found in income manager or budget view');
    }
  } catch (error) {
    console.warn('Error loading existing income sources:', error);
  }
}

// Load income sources from main budget view 
function loadIncomeFromBudgetView() {
  try {
    console.log('Loading income from main budget view...');
    const incomeItems = document.querySelectorAll('#income-items .input-group');
    
    incomeItems.forEach((item, index) => {
      const label = item.querySelector('label')?.textContent?.trim();
      const input = item.querySelector('.income-input');
      const frequencySelect = item.querySelector('.frequency-select');
      
      if (!label || !input || label === 'New Income Source') return;
      
      const amount = parseFloat(input.value) || 0;
      if (amount <= 0) return;
      
      // Get frequency and timing data
      const budgetFrequency = frequencySelect?.value || 'monthly';
      const timingPattern = item.getAttribute('data-timing-pattern') || 'day-of-month';
      const dayOfMonth = item.getAttribute('data-day-of-month') || '15';
      const dayOfWeek = item.getAttribute('data-day-of-week') || '5';
      const startDate = item.getAttribute('data-start-date') || '';
      
      // Convert budget frequency format to wizard format
      const frequencyMap = {
        'weekly': 'weekly',
        'bi-weekly': 'bi_weekly',  // Convert hyphen to underscore
        'monthly': 'monthly',
        'quarterly': 'quarterly',
        'annual': 'annually',
        'one-time': 'irregular'
      };
      const wizardFrequency = frequencyMap[budgetFrequency] || 'monthly';
      
      console.log(`Found budget income item: ${label}, amount: ${amount}, frequency: ${budgetFrequency} → ${wizardFrequency}, timing: ${timingPattern}, startDate: ${startDate}`);
      
      // Create wizard-compatible income source
      const wizardSource = {
        id: `budget-item-${index}-${Date.now()}`,
        name: label,
        type: 'salary', // Default type for budget items
        frequency: wizardFrequency,
        expectedAmount: amount,
        isGrossIncome: false, // Assume net for budget items
        taxWithholding: 0,
        paymentSchedule: {
          timingPattern: timingPattern,
          dayOfMonth: dayOfMonth,
          dayOfWeek: dayOfWeek,
          startDate: startDate
        }
      };
      
      wizardData.incomeSources.push(wizardSource);
    });
    
    console.log('Loaded income sources from budget view:', wizardData.incomeSources);
    renderIncomeSourcesList();
    
  } catch (error) {
    console.warn('Error loading income from budget view:', error);
  }
}

// Show wizard step
function showWizardStep(stepNumber) {
  currentStep = stepNumber;

  // Update step indicators
  document.querySelectorAll('.wizard-step').forEach((step, index) => {
    const stepNum = index + 1;
    step.classList.remove('active', 'completed');
    
    if (stepNum === currentStep) {
      step.classList.add('active');
    } else if (stepNum < currentStep) {
      step.classList.add('completed');
    }
  });

  // Show/hide step content
  document.querySelectorAll('.wizard-step-content').forEach((content, index) => {
    const stepNum = index + 1;
    content.style.display = stepNum === currentStep ? 'block' : 'none';
  });

  // Update navigation buttons
  const prevBtn = document.getElementById('wizard-prev-btn');
  const nextBtn = document.getElementById('wizard-next-btn');
  const finishBtn = document.getElementById('wizard-finish-btn');

  if (prevBtn) {
    prevBtn.style.display = currentStep > 1 ? 'block' : 'none';
  }

  if (nextBtn) {
    nextBtn.style.display = currentStep < 3 ? 'block' : 'none';
  }

  if (finishBtn) {
    finishBtn.style.display = currentStep === 3 ? 'block' : 'none';
  }

  // Load step-specific content
  switch (currentStep) {
    case 1:
      renderIncomeSourcesList();
      break;
    case 2:
      renderTemplateSelection();
      break;
    case 3:
      renderAllocationRules();
      break;
  }
}

// Navigate to next wizard step
export function nextWizardStep() {
  if (validateCurrentStep()) {
    let nextStep = currentStep + 1;
    
    // Skip allocation step (step 3) for blank budgets
    if (nextStep === 3 && wizardData.selectedTemplate === 'blank_budget') {
      nextStep = 4; // Skip to finish step
    }
    
    showWizardStep(nextStep);
  }
}

// Navigate to previous wizard step
export function previousWizardStep() {
  if (currentStep > 1) {
    let prevStep = currentStep - 1;
    
    // Skip allocation step (step 3) for blank budgets when going back
    if (prevStep === 3 && wizardData.selectedTemplate === 'blank_budget') {
      prevStep = 2; // Skip back to template selection
    }
    
    showWizardStep(prevStep);
  }
}

// Validate current step
function validateCurrentStep() {
  switch (currentStep) {
    case 1:
      if (wizardData.incomeSources.length === 0) {
        alert('Please add at least one income source before proceeding.');
        return false;
      }
      return true;
    case 2:
      if (!wizardData.selectedTemplate) {
        alert('Please select a budget template before proceeding.');
        return false;
      }
      return true;
    case 3:
      // Validate budget name
      const budgetNameInput = document.getElementById('budget-name');
      const budgetName = budgetNameInput ? budgetNameInput.value.trim() : '';
      if (!budgetName) {
        alert('Please enter a name for your budget before proceeding.');
        budgetNameInput?.focus();
        return false;
      }
      // Validate allocations if needed
      return true;
    default:
      return true;
  }
}


// Show income source form
export function showIncomeSourceForm(sourceId = null) {
  console.log('showIncomeSourceForm called with sourceId:', sourceId, 'type:', typeof sourceId);
  
  const form = document.getElementById('income-source-form');
  const title = document.getElementById('income-form-title');
  const addSection = document.getElementById('add-income-section');
  
  if (!form || !addSection) return;

  editingIncomeId = sourceId;
  console.log('Set editingIncomeId to:', editingIncomeId);

  // Update form title
  if (title) {
    title.textContent = sourceId ? 'Edit Income Source' : 'Add New Income Source';
  }

  // Clear or populate form
  if (sourceId) {
    populateIncomeForm(sourceId);
  } else {
    clearIncomeForm();
  }

  // Show form and expand section
  form.style.display = 'block';
  addSection.classList.add('form-expanded');
  
  // Focus on name input
  const nameInput = document.getElementById('income-name');
  if (nameInput) {
    setTimeout(() => nameInput.focus(), 100);
  }

  // Update help text
  updateAmountHelpText();
}

// Hide income source form
export function hideIncomeSourceForm() {
  const form = document.getElementById('income-source-form');
  const addSection = document.getElementById('add-income-section');
  
  if (form) {
    form.style.display = 'none';
  }
  if (addSection) {
    addSection.classList.remove('form-expanded');
  }
  editingIncomeId = null;
}

// Clear income form
function clearIncomeForm() {
  const nameEl = document.getElementById('income-name');
  const typeEl = document.getElementById('income-type');
  const frequencyEl = document.getElementById('income-frequency');
  const amountEl = document.getElementById('income-amount');
  const grossNetEl = document.getElementById('income-gross-net');
  const taxWithholdingEl = document.getElementById('income-tax-withholding');
  const timingPatternEl = document.getElementById('timing-pattern');
  const dayOfMonthEl = document.getElementById('day-of-month');
  const dayOfWeekEl = document.getElementById('day-of-week');
  const customDayEl = document.getElementById('custom-day');
  const startDateEl = document.getElementById('start-date');

  if (nameEl) nameEl.value = '';
  if (typeEl) typeEl.value = 'salary';
  if (frequencyEl) frequencyEl.value = 'monthly';
  if (amountEl) amountEl.value = '';
  if (grossNetEl) grossNetEl.value = 'true';
  if (taxWithholdingEl) taxWithholdingEl.value = '';
  if (timingPatternEl) timingPatternEl.value = 'day-of-month';
  if (dayOfMonthEl) dayOfMonthEl.value = '15';
  if (dayOfWeekEl) dayOfWeekEl.value = '5';
  if (customDayEl) customDayEl.value = '';
  if (startDateEl) startDateEl.value = '';
  
  // Update timing UI
  updateTimingOptions();
}

// Populate income form with existing data
function populateIncomeForm(sourceId) {
  const source = wizardData.incomeSources.find(s => s.id === sourceId);
  if (!source) return;

  const nameEl = document.getElementById('income-name');
  const typeEl = document.getElementById('income-type');
  const frequencyEl = document.getElementById('income-frequency');
  const amountEl = document.getElementById('income-amount');
  const grossNetEl = document.getElementById('income-gross-net');
  const taxWithholdingEl = document.getElementById('income-tax-withholding');
  const timingPatternEl = document.getElementById('timing-pattern');
  const dayOfMonthEl = document.getElementById('day-of-month');
  const dayOfWeekEl = document.getElementById('day-of-week');
  const customDayEl = document.getElementById('custom-day');
  const startDateEl = document.getElementById('start-date');

  if (nameEl) nameEl.value = source.name || '';
  if (typeEl) typeEl.value = source.type || 'salary';
  if (frequencyEl) frequencyEl.value = source.frequency || 'monthly';
  if (amountEl) amountEl.value = source.expectedAmount || '';
  if (grossNetEl) grossNetEl.value = source.isGrossIncome ? 'true' : 'false';
  if (taxWithholdingEl) taxWithholdingEl.value = source.taxWithholding || '';
  
  // Populate timing data from paymentSchedule or fallback to legacy fields
  const paymentSchedule = source.paymentSchedule || {};
  if (timingPatternEl) timingPatternEl.value = paymentSchedule.timingPattern || source.timingPattern || 'day-of-month';
  if (dayOfMonthEl) dayOfMonthEl.value = paymentSchedule.dayOfMonth || source.dayOfMonth || '15';
  if (dayOfWeekEl) dayOfWeekEl.value = paymentSchedule.dayOfWeek || source.dayOfWeek || '5';
  if (customDayEl) customDayEl.value = paymentSchedule.customDay || source.customDay || '';
  if (startDateEl) {
    const startDateValue = paymentSchedule.startDate || source.startDate;
    if (startDateValue instanceof Date) {
      startDateEl.value = startDateValue.toISOString().split('T')[0];
    } else if (startDateValue) {
      startDateEl.value = startDateValue;
    } else {
      startDateEl.value = '';
    }
  }

  // Update tax withholding visibility and timing UI
  toggleTaxWithholding();
  updateTimingOptions();
}

// Save income source
function saveIncomeSource() {
  console.log('Save income source clicked');
  
  // Get form values
  const nameEl = document.getElementById('income-name');
  const typeEl = document.getElementById('income-type');
  const frequencyEl = document.getElementById('income-frequency');
  const amountEl = document.getElementById('income-amount');
  const grossNetEl = document.getElementById('income-gross-net');
  const taxWithholdingEl = document.getElementById('income-tax-withholding');
  const timingPatternEl = document.getElementById('timing-pattern');
  const dayOfMonthEl = document.getElementById('day-of-month');
  const dayOfWeekEl = document.getElementById('day-of-week');
  const customDayEl = document.getElementById('custom-day');
  const startDateEl = document.getElementById('start-date');
  
  // Check if elements exist
  if (!nameEl || !typeEl || !frequencyEl || !amountEl || !grossNetEl || !taxWithholdingEl) {
    console.error('Some form elements are missing');
    alert('Form elements are missing. Please try again.');
    return;
  }
  
  const name = nameEl.value.trim();
  const type = typeEl.value;
  const frequency = frequencyEl.value;
  const amount = parseFloat(amountEl.value) || 0;
  const isGrossIncome = grossNetEl.value === 'true';
  const taxWithholding = parseFloat(taxWithholdingEl.value) || 0;
  
  // Get timing values
  const timingPattern = timingPatternEl?.value || 'day-of-month';
  const dayOfMonth = dayOfMonthEl?.value === 'custom' ? customDayEl?.value || '15' : dayOfMonthEl?.value || '15';
  const dayOfWeek = dayOfWeekEl?.value || '5';
  const startDate = startDateEl?.value || null;

  console.log('Form values:', { name, type, frequency, amount, isGrossIncome, taxWithholding });

  // Validate required fields
  if (!name || amount <= 0) {
    alert('Please fill in all required fields with valid values.');
    return;
  }

  // Create or update income source
  const sourceData = {
    name,
    type,
    frequency,
    expectedAmount: amount,
    isGrossIncome,
    taxWithholding: isGrossIncome ? taxWithholding : 0,
    startDate,
    paymentSchedule: {
      timingPattern,
      dayOfMonth,
      dayOfWeek,
      customDay: dayOfMonthEl?.value === 'custom' ? customDayEl?.value : null,
      startDate
    }
  };

  console.log('Creating source data:', sourceData);
  console.log('editingIncomeId:', editingIncomeId);

  if (editingIncomeId) {
    // Update existing source
    const index = wizardData.incomeSources.findIndex(s => s.id === editingIncomeId);
    console.log('Found existing source at index:', index);
    if (index >= 0) {
      // Merge paymentSchedule properly to avoid overwriting
      const existingSource = wizardData.incomeSources[index];
      wizardData.incomeSources[index] = { 
        ...existingSource, 
        ...sourceData,
        paymentSchedule: {
          ...existingSource.paymentSchedule,
          ...sourceData.paymentSchedule
        }
      };
      console.log('Updated source with merged paymentSchedule:', wizardData.incomeSources[index]);
    }
    console.log('Updated existing source');
  } else {
    // Add new source
    console.log('Adding new source');
    const newSource = new IncomeSource(sourceData);
    console.log('New source created:', newSource.toJSON());
    wizardData.incomeSources.push(newSource.toJSON());
    console.log('Added new source to array');
  }

  console.log('Current sources:', wizardData.incomeSources);

  // Update total income
  calculateTotalIncome();
  
  // Sync changes back to main budget DOM
  syncWizardChangesToBudgetDOM(sourceData, editingIncomeId);

  // Hide form and refresh list
  hideIncomeSourceForm();
  renderIncomeSourcesList();
}

// Sync wizard changes back to main budget DOM
function syncWizardChangesToBudgetDOM(sourceData, editingIncomeId) {
  try {
    // Find the corresponding budget item by name
    const budgetItems = document.querySelectorAll('#income-items .input-group');
    
    budgetItems.forEach(item => {
      const label = item.querySelector('label')?.textContent?.trim();
      
      if (label === sourceData.name) {
        console.log(`Syncing wizard changes to budget DOM for: ${sourceData.name}`);
        
        // Update timing attributes
        if (sourceData.paymentSchedule) {
          if (sourceData.paymentSchedule.timingPattern) {
            item.setAttribute('data-timing-pattern', sourceData.paymentSchedule.timingPattern);
          }
          if (sourceData.paymentSchedule.dayOfWeek) {
            item.setAttribute('data-day-of-week', sourceData.paymentSchedule.dayOfWeek);
          }
          if (sourceData.paymentSchedule.dayOfMonth) {
            item.setAttribute('data-day-of-month', sourceData.paymentSchedule.dayOfMonth);
          }
          if (sourceData.paymentSchedule.startDate) {
            item.setAttribute('data-start-date', sourceData.paymentSchedule.startDate);
            console.log(`Updated budget DOM start date to: ${sourceData.paymentSchedule.startDate}`);
          }
        }
        
        // Update amount and frequency
        const input = item.querySelector('.income-input');
        const frequencySelect = item.querySelector('.frequency-select');
        
        if (input) {
          input.value = sourceData.expectedAmount;
        }
        if (frequencySelect) {
          // Convert wizard frequency back to budget format
          const budgetFrequencyMap = {
            'weekly': 'weekly',
            'bi_weekly': 'bi-weekly',  // Convert underscore back to hyphen
            'monthly': 'monthly',
            'quarterly': 'quarterly',
            'annually': 'annual',
            'irregular': 'one-time'
          };
          const budgetFrequency = budgetFrequencyMap[sourceData.frequency] || sourceData.frequency;
          frequencySelect.value = budgetFrequency;
          console.log(`Updated budget frequency: ${sourceData.frequency} → ${budgetFrequency}`);
        }
        
        console.log('Budget DOM updated with wizard changes');
      }
    });
  } catch (error) {
    console.warn('Error syncing wizard changes to budget DOM:', error);
  }
}

// Delete income source
export function deleteIncomeSource(sourceId) {
  if (confirm('Are you sure you want to delete this income source?')) {
    // Remove from wizard data
    wizardData.incomeSources = wizardData.incomeSources.filter(s => s.id !== sourceId);
    
    // Also remove from income manager and Firestore
    incomeManager.deleteIncomeSource(sourceId).catch(error => {
      console.warn('Failed to delete income source from Firestore:', error);
    });
    
    calculateTotalIncome();
    renderIncomeSourcesList();
  }
}

// Calculate total income
function calculateTotalIncome() {
  wizardData.totalIncome = wizardData.incomeSources.reduce((total, source) => {
    const netAmount = source.expectedAmount - (source.isGrossIncome ? (source.taxWithholding || 0) : 0);
    const multipliers = {
      weekly: 4.33,
      bi_weekly: 2.17,
      monthly: 1,
      quarterly: 1/3,
      annually: 1/12,
      irregular: 1
    };
    return total + (netAmount * (multipliers[source.frequency] || 1));
  }, 0);
}

// Render income sources list
function renderIncomeSourcesList() {
  console.log('Rendering income sources list');
  const container = document.getElementById('income-sources-list');
  if (!container) {
    console.error('Income sources list container not found');
    return;
  }

  console.log('Income sources to render:', wizardData.incomeSources);

  if (wizardData.incomeSources.length === 0) {
    container.innerHTML = `
      <div class="income-empty-state">
        <div class="empty-icon">💰</div>
        <div class="empty-message">No income sources added yet</div>
        <div class="empty-submessage">Click "Add Income Source" to get started</div>
      </div>
    `;
    return;
  }

  container.innerHTML = wizardData.incomeSources.map(source => {
    const netAmount = source.expectedAmount - (source.isGrossIncome ? (source.taxWithholding || 0) : 0);
    const multipliers = {
      weekly: 4.33,
      'bi-weekly': 2.17,  // Main budget format
      bi_weekly: 2.17,    // Income manager format
      monthly: 1,
      quarterly: 1/3,
      annual: 1/12,       // Main budget format
      annually: 1/12,     // Income manager format
      'one-time': 1,      // Main budget format
      irregular: 1
    };
    const monthlyAmount = netAmount * (multipliers[source.frequency] || 1);
    
    // Debug logging for frequency conversion
    console.log(`Converting ${source.name}: ${source.frequency} × ${multipliers[source.frequency] || 1} = ${monthlyAmount}`);

    return `
      <div class="income-source-item" data-source-id="${source.id}">
        <div class="income-source-info">
          <div class="income-source-name">${source.name}</div>
          <div class="income-source-details">
            ${getIncomeTypeDisplayName(source.type)} • ${getFrequencyDisplayName(source.frequency)}${source.frequency !== 'monthly' ? ` (${formatCurrency(source.expectedAmount)} per payment)` : ''} • 
            ${source.isGrossIncome ? 'Gross income' : 'Net income'}
          </div>
        </div>
        <div class="income-source-amount">${formatCurrency(monthlyAmount)}/mo</div>
        <div class="income-source-actions">
          <button onclick="showIncomeSourceForm('${source.id}')">Edit</button>
          <button class="delete-btn" onclick="deleteIncomeSource('${source.id}')">Delete</button>
        </div>
      </div>
    `;
  }).join('');
}

// Render template selection step
function renderTemplateSelection() {
  const container = document.getElementById('template-options');
  if (!container) return;

  // Get current total monthly income
  calculateTotalIncome();
  
  container.innerHTML = Object.entries(BUDGET_TEMPLATES).map(([templateId, template]) => {
    const amounts = calculateTemplateAmounts(template, wizardData.totalIncome);
    
    // Show top 4 categories for preview
    const topCategories = Object.entries(template.categories)
      .sort((a, b) => b[1].percentage - a[1].percentage)
      .slice(0, 4);
    
    return `
      <div class="template-card ${wizardData.selectedTemplate === templateId ? 'selected' : ''}" 
           onclick="selectTemplate('${templateId}')">
        <div class="template-header">
          <div class="template-name">${template.name}</div>
          <div class="template-description">${template.description}</div>
          <div class="template-target">Best for: ${template.targetSituation}</div>
        </div>
        
        <div class="template-categories">
          <div class="template-categories-header">Top Categories:</div>
          ${topCategories.map(([categoryName, config]) => {
            const amount = amounts[categoryName];
            return `
              <div class="category-preview">
                <span class="category-name">${categoryName}</span>
                <div>
                  <span class="category-percentage">${config.percentage}%</span>
                  <div class="category-amount">${formatCurrency(amount.amount)}</div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }).join('');
  
  // Update HCOL preview if needed
  updateTemplatePreview();
}

// Select template
export function selectTemplate(templateId) {
  wizardData.selectedTemplate = templateId;
  
  // Update template cards
  document.querySelectorAll('.template-card').forEach(card => {
    card.classList.remove('selected');
  });
  
  const selectedCard = document.querySelector(`[onclick="selectTemplate('${templateId}')"]`);
  if (selectedCard) {
    selectedCard.classList.add('selected');
  }
  
  // Update preview
  updateTemplatePreview();
}

// Toggle High Cost of Living
export function toggleHighCostLiving() {
  const checkbox = document.getElementById('high-cost-living');
  wizardData.isHighCostLiving = checkbox.checked;
  updateTemplatePreview();
}

// Update template preview
function updateTemplatePreview() {
  const previewContainer = document.getElementById('selected-template-preview');
  if (!previewContainer || !wizardData.selectedTemplate) {
    previewContainer.style.display = 'none';
    return;
  }
  
  const baseTemplate = getTemplate(wizardData.selectedTemplate);
  const adjustedTemplate = getTemplateWithHCOL(wizardData.selectedTemplate, wizardData.isHighCostLiving);
  
  if (!wizardData.isHighCostLiving) {
    previewContainer.style.display = 'none';
    return;
  }
  
  previewContainer.style.display = 'block';
  
  // Find categories that changed
  const changes = [];
  Object.entries(adjustedTemplate.categories).forEach(([categoryName, config]) => {
    const baseConfig = baseTemplate.categories[categoryName];
    if (baseConfig && config.percentage !== baseConfig.percentage) {
      const isIncrease = config.percentage > baseConfig.percentage;
      changes.push({
        category: categoryName,
        change: `${baseConfig.percentage}% → ${config.percentage}%`,
        isIncrease
      });
    }
  });
  
  previewContainer.innerHTML = `
    <div class="preview-header">
      📍 High Cost of Living Adjustments Applied
    </div>
    <div class="preview-adjustments">
      ${changes.map(change => `
        <div class="adjustment-item">
          <span class="adjustment-category">${change.category}</span>
          <span class="adjustment-change ${change.isIncrease ? 'increase' : 'decrease'}">
            ${change.change}
          </span>
        </div>
      `).join('')}
    </div>
  `;
}

// Render allocation rules step
function renderAllocationRules() {
  // Populate budget name with suggested name if empty
  const budgetNameInput = document.getElementById('budget-name');
  if (budgetNameInput && !budgetNameInput.value.trim()) {
    const suggestedName = `${wizardData.finalTemplate?.name || 'Custom'} Budget`;
    budgetNameInput.value = suggestedName;
  }
  
  const container = document.getElementById('allocation-sources');
  if (!container) return;

  if (wizardData.incomeSources.length === 0) {
    container.innerHTML = `
      <div class="income-empty-state">
        <div class="empty-icon">📊</div>
        <div class="empty-message">No income sources to configure</div>
        <div class="empty-submessage">Go back and add some income sources first</div>
      </div>
    `;
    return;
  }

  // Get categories from selected template, falling back to existing budget data
  const categories = getTemplateCategories();

  const totalMonthlyIncome = wizardData.incomeSources.reduce((sum, source) => sum + calculateSourceMonthlyAmount(source), 0);
  const incomeSourcesLabel = wizardData.incomeSources.length === 1
    ? wizardData.incomeSources[0].name
    : `${wizardData.incomeSources.length} Income Sources Combined`;

  container.innerHTML = `
    <div class="allocation-source">
      <div class="allocation-source-header">
        ${incomeSourcesLabel} - ${formatCurrency(totalMonthlyIncome)}/month total
      </div>
      <div class="allocation-rules" id="allocation-rules-combined">
        ${categories.map(category => renderAllocationRuleItem({ id: 'combined' }, category, totalMonthlyIncome)).join('')}
      </div>
      <div class="allocation-summary" id="allocation-summary-combined">
        <div class="summary-item">
          <span>Total Allocated:</span>
          <span id="total-allocated-combined">${formatCurrency(0)}</span>
        </div>
        <div class="summary-item">
          <span>Remaining:</span>
          <span id="remaining-combined">${formatCurrency(totalMonthlyIncome)}</span>
        </div>
      </div>
    </div>
  `;
  
  // Auto-populate with template values if available
  if (wizardData.finalTemplate) {
    setTimeout(() => applyTemplateAllocations(), 200);
  }
}

// Apply template allocations to combined income panel
function applyTemplateAllocations() {
  if (!wizardData.finalTemplate) return;

  const sourceId = 'combined';

  if (!wizardData.allocations[sourceId]) {
    wizardData.allocations[sourceId] = {};
  }

  Object.entries(wizardData.finalTemplate.categories).forEach(([categoryName, config]) => {
    const methodSelect = document.querySelector(`select[data-source="${sourceId}"][data-category="${categoryName}"]`);
    if (methodSelect) {
      methodSelect.value = 'percentage';
      changeLineItemMethod(sourceId, categoryName, 'percentage');
    }
  });

  setTimeout(() => {
    Object.entries(wizardData.finalTemplate.categories).forEach(([categoryName, config]) => {
      const percentageInput = document.querySelector(`input[data-source="${sourceId}"][data-category="${categoryName}"][data-method="percentage"]`);
      if (percentageInput) {
        percentageInput.value = config.percentage;
        updateAllocation(sourceId, categoryName, config.percentage, 'percentage', true);
      }
    });

    updateAllocationSummary(sourceId);
  }, 200);
}

// Calculate source monthly amount
function calculateSourceMonthlyAmount(source) {
  const netAmount = source.expectedAmount - (source.isGrossIncome ? (source.taxWithholding || 0) : 0);
  const multipliers = {
    weekly: 4.33,
    bi_weekly: 2.17,
    monthly: 1,
    quarterly: 1/3,
    annually: 1/12,
    irregular: 1
  };
  return netAmount * (multipliers[source.frequency] || 1);
}

// Render individual allocation rule item with per-line method selection
function renderAllocationRuleItem(source, category, monthlyAmount) {
  const existingAmount = getExistingCategoryAmount(category);
  const encodedCategory = encodeCategoryName(category);
  const allocationKey = `${source.id}-${encodedCategory}`;
  
  return `
    <div class="allocation-rule-item" data-source="${source.id}" data-category="${category}">
      <div class="rule-category-name">
        <span class="category-label">${category}</span>
        ${existingAmount > 0 ? `<span class="existing-indicator" title="Has existing budget: ${formatCurrency(existingAmount)}">📊</span>` : ''}
      </div>
      
      <div class="rule-method-selector">
        <select class="method-select" data-source="${source.id}" data-category="${category}" 
                onchange="changeLineItemMethod('${source.id}', '${category}', this.value)">
          <option value="none">No allocation</option>
          <option value="percentage">Percentage</option>
          <option value="fixed_amount">Fixed amount</option>
          ${existingAmount > 0 ? `<option value="existing_expenses">Use existing (${formatCurrency(existingAmount)})</option>` : ''}
        </select>
      </div>
      
      <div class="rule-input-container" id="input-container-${allocationKey}">
        <!-- Input field will be populated based on method -->
      </div>
      
      <div class="rule-result" id="result-${allocationKey}">
        <span class="result-amount">$0</span>
        <span class="result-percentage">0%</span>
      </div>
    </div>
  `;
}

// Render allocation rules for a specific source and method
function renderAllocationRulesForSource(source, categories, method) {
  switch (method) {
    case 'percentage':
      return categories.map(category => `
        <div class="allocation-rule">
          <div class="rule-category">${category}</div>
          <div class="rule-input">
            <input type="number" 
                   min="0" 
                   max="100" 
                   step="1" 
                   placeholder="0%" 
                   data-source="${source.id}" 
                   data-category="${category}"
                   data-method="percentage"
                   onchange="updateAllocation('${source.id}', '${category}', this.value, 'percentage')">
            <span class="input-suffix">%</span>
          </div>
          <div class="rule-amount" id="amount-${source.id}-${category}">
            ${formatCurrency(0)}
          </div>
        </div>
      `).join('');
      
    case 'fixed_amount':
      return categories.map(category => `
        <div class="allocation-rule">
          <div class="rule-category">${category}</div>
          <div class="rule-input">
            <span class="input-prefix">$</span>
            <input type="number" 
                   min="0" 
                   step="0.01" 
                   placeholder="0.00" 
                   data-source="${source.id}" 
                   data-category="${category}"
                   data-method="fixed_amount"
                   onchange="updateAllocation('${source.id}', '${category}', this.value, 'fixed_amount')">
          </div>
          <div class="rule-percentage" id="percentage-${source.id}-${category}">
            0%
          </div>
        </div>
      `).join('');
      
    case 'existing_expenses':
      return categories.map(category => {
        const existingAmount = getExistingCategoryAmount(category);
        return `
          <div class="allocation-rule existing-expense">
            <div class="rule-category">${category}</div>
            <div class="rule-existing-amount">
              ${formatCurrency(existingAmount)}
              ${existingAmount > 0 ? '(from current budget)' : '(no expenses found)'}
            </div>
            <div class="rule-toggle">
              <label class="toggle-switch">
                <input type="checkbox" 
                       data-source="${source.id}" 
                       data-category="${category}"
                       data-method="existing_expenses"
                       ${existingAmount > 0 ? 'checked' : ''}
                       onchange="updateAllocation('${source.id}', '${category}', '${existingAmount}', 'existing_expenses', this.checked)">
                <span class="toggle-slider"></span>
              </label>
            </div>
          </div>
        `;
      }).join('');
      
    default:
      return '';
  }
}

// Get categories from selected template or fallback
function getTemplateCategories() {
  if (wizardData.selectedTemplate) {
    // Get final template with HCOL adjustments
    const finalTemplate = getTemplateWithHCOL(wizardData.selectedTemplate, wizardData.isHighCostLiving);
    wizardData.finalTemplate = finalTemplate;
    return Object.keys(finalTemplate.categories);
  }
  
  // Fall back to existing budget data
  return getBudgetCategories();
}

// Get budget categories from current budget data
function getBudgetCategories() {
  const budgetData = getCurrentBudgetData();
  
  if (budgetData && budgetData.categories && budgetData.categories.length > 0) {
    // Extract category names from existing budget
    return budgetData.categories.map(category => category.name);
  } else {
    // Fallback to default categories if no budget exists
    return [
      'Housing', 'Transportation', 'Food', 'Utilities', 'Insurance',
      'Healthcare', 'Savings', 'Entertainment', 'Personal', 'Other'
    ];
  }
}

// Get existing category amount from current budget
function getExistingCategoryAmount(categoryName) {
  const budgetData = getCurrentBudgetData();
  
  if (budgetData && budgetData.categories) {
    // Find the category in the current budget
    const category = budgetData.categories.find(cat => cat.name === categoryName);
    
    if (category && category.items) {
      // Sum all items in this category
      return category.items.reduce((total, item) => {
        return total + (parseFloat(item.amount) || 0);
      }, 0);
    }
  }
  
  // Return 0 if category not found or no budget data
  return 0;
}

// Get all category amounts from current budget for summary
function getAllExistingCategoryAmounts() {
  const budgetData = getCurrentBudgetData();
  const categoryAmounts = {};
  
  if (budgetData && budgetData.categories) {
    budgetData.categories.forEach(category => {
      if (category.items) {
        const total = category.items.reduce((sum, item) => {
          return sum + (parseFloat(item.amount) || 0);
        }, 0);
        categoryAmounts[category.name] = total;
      }
    });
  }
  
  return categoryAmounts;
}

// Change allocation method for a specific line item
export function changeLineItemMethod(sourceId, category, method) {
  const encodedCategory = encodeCategoryName(category);
  const allocationKey = `${sourceId}-${encodedCategory}`;
  const inputContainer = document.getElementById(`input-container-${allocationKey}`);

  if (!inputContainer) return;
  
  // Clear existing allocation for this category
  if (!wizardData.allocations[sourceId]) {
    wizardData.allocations[sourceId] = {};
  }
  
  if (method === 'none') {
    delete wizardData.allocations[sourceId][category];
    inputContainer.innerHTML = '';
  } else {
    // Render appropriate input based on method
    inputContainer.innerHTML = renderMethodInput(sourceId, category, method);
    
    // Auto-fill for existing expenses method
    if (method === 'existing_expenses') {
      const existingAmount = getExistingCategoryAmount(category);
      updateAllocation(sourceId, category, existingAmount, method, true);
    }
  }
  
  // Update result display
  updateLineItemResult(sourceId, category);
  updateAllocationSummary(sourceId);
}

// Render input field for specific method
function renderMethodInput(sourceId, category, method) {
  const encodedCategory = encodeCategoryName(category);
  const allocationKey = `${sourceId}-${encodedCategory}`;
  
  switch (method) {
    case 'percentage':
      return `
        <div class="input-field percentage-input">
          <input type="text" 
                 placeholder="0" 
                 data-source="${sourceId}" 
                 data-category="${category}"
                 data-method="percentage"
                 oninput="handleMathInput(this)"
                 onblur="processAllocationInput(this)"
                 onkeypress="if(event.key==='Enter') processAllocationInput(this)">
          <span class="input-suffix">%</span>
        </div>
      `;
      
    case 'fixed_amount':
      return `
        <div class="input-field amount-input">
          <span class="input-prefix">$</span>
          <input type="text" 
                 placeholder="0.00" 
                 data-source="${sourceId}" 
                 data-category="${category}"
                 data-method="fixed_amount"
                 oninput="handleMathInput(this)"
                 onblur="processAllocationInput(this)"
                 onkeypress="if(event.key==='Enter') processAllocationInput(this)">
        </div>
      `;
      
    case 'existing_expenses':
      const existingAmount = getExistingCategoryAmount(category);
      return `
        <div class="input-field existing-input">
          <span class="existing-amount">${formatCurrency(existingAmount)}</span>
          <span class="existing-label">(from budget)</span>
        </div>
      `;
      
    default:
      return '';
  }
}

// Handle math input with live preview
export function handleMathInput(input) {
  const originalValue = input.value;
  const mathResult = evaluateMathExpression(originalValue);
  
  // Show math preview if it's a valid expression and different from the input
  if (mathResult !== null && originalValue.includes('+', '-', '*', '/') && mathResult.toString() !== originalValue) {
    input.title = `= ${mathResult}`;
    input.style.borderColor = '#17a2b8'; // Info color for math preview
  } else {
    input.title = '';
    input.style.borderColor = '';
  }
}

// Process allocation input when user finishes editing
export function processAllocationInput(input) {
  const sourceId = input.dataset.source;
  const category = input.dataset.category;
  const method = input.dataset.method;
  let value = input.value;
  
  // Evaluate math expression if present
  const mathResult = evaluateMathExpression(value);
  if (mathResult !== null) {
    value = mathResult;
    input.value = value; // Update the input to show the calculated result
  }
  
  // Update allocation
  const numValue = parseFloat(value) || 0;
  updateAllocation(sourceId, category, numValue, method, true);
  
  // Clear math preview styling
  input.title = '';
  input.style.borderColor = '';
}

// Update allocation
export function updateAllocation(sourceId, category, value, method, isEnabled = true) {
  const numValue = parseFloat(value) || 0;
  
  if (!wizardData.allocations[sourceId]) {
    wizardData.allocations[sourceId] = {};
  }
  
  if (method === 'existing_expenses' && !isEnabled) {
    // Remove allocation if checkbox is unchecked
    delete wizardData.allocations[sourceId][category];
  } else {
    // Store allocation with method and value
    wizardData.allocations[sourceId][category] = {
      method: method,
      value: numValue
    };
  }
  
  // Update line item result display
  updateLineItemResult(sourceId, category);
  
  // Update summary
  updateAllocationSummary(sourceId);
}

// Update individual line item result display
function updateLineItemResult(sourceId, category) {
  let monthlyAmount;
  if (sourceId === 'combined') {
    monthlyAmount = wizardData.incomeSources.reduce((sum, s) => sum + calculateSourceMonthlyAmount(s), 0);
  } else {
    const source = wizardData.incomeSources.find(s => s.id === sourceId);
    if (!source) {
      console.warn(`Source not found: ${sourceId}`);
      return;
    }
    monthlyAmount = calculateSourceMonthlyAmount(source);
  }
  const allocation = wizardData.allocations[sourceId]?.[category];
  const encodedCategory = encodeCategoryName(category);
  const allocationKey = `${sourceId}-${encodedCategory}`;
  
  const amountEl = document.querySelector(`#result-${allocationKey} .result-amount`);
  const percentageEl = document.querySelector(`#result-${allocationKey} .result-percentage`);
  
  console.log(`Updating result for ${category}:`, {
    sourceId,
    category,
    encodedCategory,
    allocationKey,
    monthlyAmount,
    allocation,
    amountEl: !!amountEl,
    percentageEl: !!percentageEl
  });
  
  if (!amountEl || !percentageEl) {
    console.warn(`Result elements not found for ${allocationKey}`);
    return;
  }
  
  if (!allocation) {
    amountEl.textContent = '$0';
    percentageEl.textContent = '0%';
    return;
  }
  
  let dollarAmount = 0;
  let percentage = 0;
  
  if (allocation.method === 'percentage') {
    dollarAmount = (monthlyAmount * allocation.value) / 100;
    percentage = allocation.value;
  } else if (allocation.method === 'fixed_amount') {
    dollarAmount = allocation.value;
    percentage = monthlyAmount > 0 ? (allocation.value / monthlyAmount) * 100 : 0;
  } else if (allocation.method === 'existing_expenses') {
    dollarAmount = allocation.value;
    percentage = monthlyAmount > 0 ? (allocation.value / monthlyAmount) * 100 : 0;
  }
  
  amountEl.textContent = formatCurrency(dollarAmount);
  percentageEl.textContent = `${percentage.toFixed(1)}%`;
  
  // Color coding
  if (allocation.method === 'percentage' && allocation.value > 100) {
    percentageEl.style.color = '#dc3545'; // Red for over 100%
  } else if (dollarAmount > monthlyAmount) {
    amountEl.style.color = '#dc3545'; // Red for over monthly amount
  } else {
    amountEl.style.color = '#28a745'; // Green for valid amounts
    percentageEl.style.color = '#6c757d'; // Gray for percentages
  }
}

// Update allocation summary for a source
function updateAllocationSummary(sourceId) {
  let monthlyAmount;
  if (sourceId === 'combined') {
    monthlyAmount = wizardData.incomeSources.reduce((sum, s) => sum + calculateSourceMonthlyAmount(s), 0);
  } else {
    const source = wizardData.incomeSources.find(s => s.id === sourceId);
    if (!source) return;
    monthlyAmount = calculateSourceMonthlyAmount(source);
  }
  const allocations = wizardData.allocations[sourceId] || {};
  
  let totalAllocated = 0;
  
  for (const [category, allocation] of Object.entries(allocations)) {
    if (allocation.method === 'percentage') {
      totalAllocated += (monthlyAmount * allocation.value) / 100;
    } else if (allocation.method === 'fixed_amount') {
      totalAllocated += allocation.value;
    } else if (allocation.method === 'existing_expenses') {
      totalAllocated += allocation.value;
    }
  }
  
  const remaining = monthlyAmount - totalAllocated;
  
  // Update display elements
  const totalAllocatedEl = document.getElementById(`total-allocated-${sourceId}`);
  const remainingEl = document.getElementById(`remaining-${sourceId}`);
  
  if (totalAllocatedEl) {
    totalAllocatedEl.textContent = formatCurrency(totalAllocated);
    totalAllocatedEl.style.color = totalAllocated > monthlyAmount ? '#dc3545' : '#28a745';
  }
  
  if (remainingEl) {
    remainingEl.textContent = formatCurrency(remaining);
    remainingEl.style.color = remaining < 0 ? '#dc3545' : '#6c757d';
  }
}



function renderTemplateSummary() {
  const container = document.getElementById('template-summary');
  if (!container) return;

  if (!wizardData.selectedTemplate) {
    container.innerHTML = '<div class="summary-header">⚠️ No Template Selected</div>';
    return;
  }

  const template = wizardData.finalTemplate || getTemplate(wizardData.selectedTemplate);
  
  container.innerHTML = `
    <div class="summary-header">📋 Selected Template: ${template.name}</div>
    <div class="summary-item">
      <div class="summary-label">Template Type</div>
      <div class="summary-value">${template.description}</div>
    </div>
    ${wizardData.isHighCostLiving ? `
      <div class="summary-item">
        <div class="summary-label">Adjustments</div>
        <div class="summary-value" style="color: #4CAF50;">High Cost of Living Applied</div>
      </div>
    ` : ''}
    <div class="summary-item">
      <div class="summary-label">Total Categories</div>
      <div class="summary-value">${Object.keys(template.categories).length}</div>
    </div>
  `;
}

// Render allocation summary
function renderAllocationSummary() {
  const container = document.getElementById('allocation-summary');
  if (!container) return;

  // Calculate total allocations by category
  const categoryTotals = {};
  
  Object.entries(wizardData.allocations).forEach(([sourceId, allocations]) => {
    const source = wizardData.incomeSources.find(s => s.id === sourceId);
    if (!source) return;
    
    const monthlyAmount = calculateSourceMonthlyAmount(source);
    
    Object.entries(allocations).forEach(([category, percentage]) => {
      if (percentage > 0) {
        const amount = (monthlyAmount * percentage) / 100;
        categoryTotals[category] = (categoryTotals[category] || 0) + amount;
      }
    });
  });

  const totalAllocated = Object.values(categoryTotals).reduce((sum, amount) => sum + amount, 0);
  const unallocated = wizardData.totalIncome - totalAllocated;

  container.innerHTML = `
    <div class="summary-header">📊 Budget Allocations</div>
    ${Object.entries(categoryTotals).map(([category, amount]) => `
      <div class="summary-item">
        <div class="summary-label">${category}</div>
        <div class="summary-value">${formatCurrency(amount)}</div>
      </div>
    `).join('')}
    <div class="summary-item">
      <div class="summary-label">Unallocated</div>
      <div class="summary-value">${formatCurrency(unallocated)}</div>
    </div>
    <div class="summary-item summary-total">
      <div class="summary-label">Total Allocated</div>
      <div class="summary-value">${formatCurrency(totalAllocated)}</div>
    </div>
  `;
}

// Render forecast preview
function renderForecastPreview() {
  const container = document.getElementById('forecast-preview');
  if (!container) return;

  container.innerHTML = `
    <div class="summary-header">📈 3-Month Income Forecast</div>
    <div class="summary-item">
      <div class="summary-label">Next Month</div>
      <div class="summary-value positive">${formatCurrency(wizardData.totalIncome)}</div>
    </div>
    <div class="summary-item">
      <div class="summary-label">Month 2</div>
      <div class="summary-value positive">${formatCurrency(wizardData.totalIncome)}</div>
    </div>
    <div class="summary-item">
      <div class="summary-label">Month 3</div>
      <div class="summary-value positive">${formatCurrency(wizardData.totalIncome)}</div>
    </div>
    <div class="summary-item summary-total">
      <div class="summary-label">3-Month Total</div>
      <div class="summary-value positive">${formatCurrency(wizardData.totalIncome * 3)}</div>
    </div>
  `;
}

// Finish budget creation wizard
export async function finishIncomeWizard() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    alert('You must be signed in to create a budget.');
    return;
  }

  if (!confirm('Create your personalized budget with this configuration?')) {
    return;
  }

  try {
    // 1. Get budget name from input field
    const budgetNameInput = document.getElementById('budget-name');
    const budgetName = budgetNameInput ? budgetNameInput.value.trim() : '';
    if (!budgetName) {
      alert('Please enter a name for your budget before proceeding.');
      budgetNameInput?.focus();
      return;
    }

    // 2. Save income sources to income manager
    incomeManager.incomeSources.clear();
    wizardData.incomeSources.forEach(sourceData => {
      const source = incomeManager.addIncomeSource(sourceData);
      
      // Apply allocations if any
      if (wizardData.allocations[source.id]) {
        Object.entries(wizardData.allocations[source.id]).forEach(([category, allocation]) => {
          if (allocation && allocation.value > 0) {
            source.setAllocation(category, allocation.value, allocation.method);
          }
        });
      }
    });
    incomeManager.saveData();

    // 3. Create budget data structure compatible with existing system
    const budgetData = createBudgetFromWizardData();

    // 4. Save as new budget version (this updates local state AND saves to Firebase)
    await saveBudgetVersion(budgetName, budgetData);

    // 5. Load it as the current version — sets currentVersionName and restores UI
    loadBudgetVersion(budgetName, false);
    
    // 6. Check if this should be set as default
    const shouldSetDefault = await promptForDefaultSetting(budgetName);
    if (shouldSetDefault) {
      setDefaultVersion(budgetName);
    }

    // 7. Show success message
    alert(`✨ Budget "${budgetName}" created successfully!\n\n📊 Template: ${wizardData.finalTemplate?.name || 'Custom'}\n💰 Total Monthly Income: ${formatCurrency(wizardData.totalIncome)}\n📋 Categories: ${Object.keys(budgetData.categories).length}`);

    // 8. Close wizard
    closeIncomeWizard();

    // 9. Refresh budget display and welcome screen
    if (typeof calculateTotals === 'function') {
      calculateTotals();
    }
    updateWelcomeScreen();

    // 10. Initialize getting-started checklist for this new user
    if (typeof window.initializeGettingStarted === 'function') {
      const user = getCurrentUser();
      if (user) {
        await window.initializeGettingStarted(user.uid, true);
      }
    }

    // 11. Switch to the budget view after creation
    // Import the view switching function from app.js
    if (typeof window.switchToSingleView === 'function') {
      window.switchToSingleView();
    }

  } catch (error) {
    console.error('Error creating budget:', error);
    alert('Error creating budget. Please try again.');
  }
}

// Check if user wants to set the new budget as default
async function promptForDefaultSetting(budgetName) {
  // Get the checkbox value from the wizard
  const checkbox = document.getElementById('set-as-default-budget');
  return checkbox ? checkbox.checked : false;
}

// Get target percentage for a category from the selected template
function getTargetPercentageForCategory(categoryName) {
  if (!wizardData.finalTemplate || !wizardData.finalTemplate.categories) {
    return 0;
  }
  
  const categoryConfig = wizardData.finalTemplate.categories[categoryName];
  return categoryConfig ? categoryConfig.percentage : 0;
}

// Create budget data structure from wizard configuration
function createBudgetFromWizardData() {
  // Create budget data structure compatible with existing budget system
  const income = wizardData.incomeSources.map(source => {
    const incomeItem = {
      label: source.name,
      amount: calculateSourceMonthlyAmount(source),
      frequency: source.frequency || 'monthly'
    };
    
    // Include timing/scheduling data if it exists
    if (source.paymentSchedule) {
      if (source.paymentSchedule.timingPattern) {
        incomeItem.timingPattern = source.paymentSchedule.timingPattern;
      }
      if (source.paymentSchedule.dayOfWeek) {
        incomeItem.dayOfWeek = parseInt(source.paymentSchedule.dayOfWeek);
      }
      if (source.paymentSchedule.dayOfMonth) {
        incomeItem.dayOfMonth = source.paymentSchedule.dayOfMonth;
      }
      if (source.paymentSchedule.startDate) {
        incomeItem.startDate = source.paymentSchedule.startDate;
      }
    }
    
    return incomeItem;
  });
  
  // Handle blank budgets differently
  if (wizardData.selectedTemplate === 'blank_budget') {
    return {
      income: income,
      categories: [
        {
          name: 'New Category',
          items: [
            {
              label: 'Sample Expense',
              amount: 0
            }
          ]
        }
      ]
    };
  }
  
  // For non-blank budgets, calculate allocations and create categories
  const categoryTotals = {};
  const totalMonthlyIncome = wizardData.incomeSources.reduce((sum, source) => sum + calculateSourceMonthlyAmount(source), 0);

  const combinedAllocations = wizardData.allocations['combined'] || {};
  if (Object.keys(combinedAllocations).length > 0) {
    // Single combined allocation panel (new mode)
    Object.entries(combinedAllocations).forEach(([categoryName, allocation]) => {
      let allocatedAmount = 0;
      if (allocation.method === 'percentage') {
        allocatedAmount = (totalMonthlyIncome * allocation.value) / 100;
      } else if (allocation.method === 'fixed_amount') {
        allocatedAmount = allocation.value;
      } else if (allocation.method === 'existing_expenses') {
        allocatedAmount = allocation.value;
      }
      if (allocatedAmount > 0) {
        categoryTotals[categoryName] = (categoryTotals[categoryName] || 0) + allocatedAmount;
      }
    });
  } else {
    // Fallback: per-source allocations (legacy)
    wizardData.incomeSources.forEach(source => {
      const monthlyAmount = calculateSourceMonthlyAmount(source);
      const sourceAllocations = wizardData.allocations[source.id] || {};

      Object.entries(sourceAllocations).forEach(([categoryName, allocation]) => {
        if (!categoryTotals[categoryName]) {
          categoryTotals[categoryName] = 0;
        }

        let allocatedAmount = 0;
        if (allocation.method === 'percentage') {
          allocatedAmount = (monthlyAmount * allocation.value) / 100;
        } else if (allocation.method === 'fixed_amount') {
          allocatedAmount = allocation.value;
        } else if (allocation.method === 'existing_expenses') {
          allocatedAmount = allocation.value;
        }

        categoryTotals[categoryName] += allocatedAmount;
      });
    });
  }
  
  const categories = Object.entries(categoryTotals).map(([categoryName, totalAmount]) => {
    // Get default items for this category
    const defaultItems = DEFAULT_EXPENSE_ITEMS[categoryName] || [`${categoryName} Item`];
    
    // Create items with $0 amounts as placeholders
    const items = defaultItems.map(itemName => ({
      label: itemName,
      amount: 0,
      frequency: 'monthly',
      isTaxDeductible: false,
      isRecurring: false
    }));
    
    return {
      name: categoryName,
      items: items,
      // Store the target amount and type for this category
      targetType: 'percentage',
      targetValue: getTargetPercentageForCategory(categoryName)
    };
  });
  
  return {
    income: income,
    categories: categories
  };
}

// Fallback function to manually populate budget UI
function populateBudgetFromWizardData(budgetData) {
  console.warn('Budget integration not fully implemented - budget data created:', budgetData);
  // TODO: Integrate with existing budget module to actually populate the main budget UI
}

// Toggle tax withholding visibility
function toggleTaxWithholding() {
  const select = document.getElementById('income-gross-net');
  const group = document.getElementById('tax-withholding-group');
  
  if (select && group) {
    const isGross = select.value === 'true';
    if (isGross) {
      group.classList.remove('hidden');
    } else {
      group.classList.add('hidden');
      document.getElementById('income-tax-withholding').value = '';
    }
  }
}

// Update amount help text based on frequency
function updateAmountHelpText() {
  const frequencySelect = document.getElementById('income-frequency');
  const helpText = document.getElementById('income-amount-help');
  
  if (frequencySelect && helpText) {
    const frequency = frequencySelect.value;
    const frequencyName = getFrequencyDisplayName(frequency);
    helpText.textContent = `Enter amount per ${frequencyName.toLowerCase()} payment`;
  }
}

// Export global functions for HTML onclick handlers
window.openIncomeWizard = openIncomeWizard;
window.closeIncomeWizard = closeIncomeWizard;
window.nextWizardStep = nextWizardStep;
window.previousWizardStep = previousWizardStep;
window.showIncomeSourceForm = showIncomeSourceForm;
window.hideIncomeSourceForm = hideIncomeSourceForm;
window.deleteIncomeSource = deleteIncomeSource;
window.selectTemplate = selectTemplate;
window.toggleHighCostLiving = toggleHighCostLiving;
window.updateAllocation = updateAllocation;
window.changeLineItemMethod = changeLineItemMethod;
window.handleMathInput = handleMathInput;
window.processAllocationInput = processAllocationInput;
window.finishIncomeWizard = finishIncomeWizard;

// Timing functions for income wizard
window.updateTimingOptions = updateTimingOptions;
window.updateTimingInputs = updateTimingInputs;
window.updateNextPaymentDisplay = updateNextPaymentDisplay;

// Update timing options based on frequency
function updateTimingOptions() {
  const frequencyEl = document.getElementById('income-frequency');
  const timingGroupEl = document.getElementById('timing-group');
  const timingPatternEl = document.getElementById('timing-pattern');
  
  if (!frequencyEl || !timingGroupEl) return;
  
  const frequency = frequencyEl.value;
  
  // Hide timing for irregular payments
  if (frequency === 'irregular') {
    timingGroupEl.style.display = 'none';
    return;
  } else {
    timingGroupEl.style.display = 'block';
  }
  
  // Update pattern options based on frequency
  if (timingPatternEl) {
    if (frequency === 'weekly' || frequency === 'bi_weekly') {
      timingPatternEl.value = 'day-of-week';
      // Hide day-of-month option for weekly/bi-weekly
      const dayOfMonthOption = timingPatternEl.querySelector('option[value="day-of-month"]');
      if (dayOfMonthOption) dayOfMonthOption.style.display = 'none';
      const dayOfWeekOption = timingPatternEl.querySelector('option[value="day-of-week"]');
      if (dayOfWeekOption) dayOfWeekOption.style.display = 'block';
    } else {
      // Show both options for monthly, quarterly, annually
      const dayOfMonthOption = timingPatternEl.querySelector('option[value="day-of-month"]');
      if (dayOfMonthOption) dayOfMonthOption.style.display = 'block';
      const dayOfWeekOption = timingPatternEl.querySelector('option[value="day-of-week"]');
      if (dayOfWeekOption) dayOfWeekOption.style.display = 'block';
    }
  }
  
  updateTimingInputs();
}

// Update timing inputs based on selected pattern
function updateTimingInputs() {
  const timingPatternEl = document.getElementById('timing-pattern');
  const dayOfMonthEl = document.getElementById('day-of-month');
  const dayOfWeekEl = document.getElementById('day-of-week');
  const customDayEl = document.getElementById('custom-day');
  const startDateEl = document.getElementById('start-date');
  
  if (!timingPatternEl) return;
  
  const pattern = timingPatternEl.value;
  
  // Show/hide appropriate select based on pattern
  if (dayOfMonthEl) {
    dayOfMonthEl.style.display = pattern === 'day-of-month' ? 'inline-block' : 'none';
  }
  if (dayOfWeekEl) {
    dayOfWeekEl.style.display = pattern === 'day-of-week' ? 'inline-block' : 'none';
  }
  if (startDateEl) {
    startDateEl.style.display = pattern === 'day-of-week' ? 'inline-block' : 'none';
  }
  
  // Handle custom day input
  if (dayOfMonthEl && customDayEl) {
    customDayEl.style.display = dayOfMonthEl.value === 'custom' ? 'inline-block' : 'none';
  }
  
  updateNextPaymentDisplay();
}

// Update next payment display
function updateNextPaymentDisplay() {
  const frequencyEl = document.getElementById('income-frequency');
  const timingPatternEl = document.getElementById('timing-pattern');
  const dayOfMonthEl = document.getElementById('day-of-month');
  const dayOfWeekEl = document.getElementById('day-of-week');
  const customDayEl = document.getElementById('custom-day');
  const startDateEl = document.getElementById('start-date');
  const nextPaymentEl = document.getElementById('next-payment-display');
  
  if (!nextPaymentEl || !frequencyEl) return;
  
  const frequency = frequencyEl.value;
  
  if (frequency === 'irregular') {
    nextPaymentEl.textContent = 'Next payment: Variable timing';
    return;
  }
  
  const pattern = timingPatternEl?.value || 'day-of-month';
  const dayOfMonth = dayOfMonthEl?.value === 'custom' ? customDayEl?.value || '15' : dayOfMonthEl?.value || '15';
  const dayOfWeek = dayOfWeekEl?.value || '5';
  const startDate = startDateEl?.value || null;
  
  const nextPayment = calculateWizardNextPaymentDate(frequency, pattern, dayOfMonth, dayOfWeek, startDate);
  nextPaymentEl.textContent = `Next payment: ${nextPayment}`;
}

// Calculate next payment date for wizard
function calculateWizardNextPaymentDate(frequency, pattern, dayOfMonth, dayOfWeek, startDate) {
  const today = new Date();
  let nextPayment = new Date(today);
  
  if (pattern === 'day-of-week') {
    const targetDay = parseInt(dayOfWeek);
    
    // If start date is provided, use it as reference for day-of-week calculations
    if (startDate) {
      const startDateObj = new Date(startDate);
      const startDayOfWeek = startDateObj.getDay() || 7; // Convert Sunday (0) to 7
      
      // If start date is in the future, use it as the next payment
      if (startDateObj > today) {
        nextPayment = new Date(startDateObj);
      } else {
        // Calculate next occurrence based on frequency from start date
        const weeksSinceStart = Math.floor((today - startDateObj) / (7 * 24 * 60 * 60 * 1000));
        const weeksToAdd = frequency === 'weekly' ? 1 : 2;
        const nextWeek = weeksSinceStart + (weeksSinceStart % weeksToAdd === 0 ? weeksToAdd : weeksToAdd - (weeksSinceStart % weeksToAdd));
        
        nextPayment = new Date(startDateObj);
        nextPayment.setDate(startDateObj.getDate() + (nextWeek * 7));
      }
    } else {
      // Original logic without start date
      const currentDay = today.getDay() || 7; // Convert Sunday (0) to 7
      const daysUntilTarget = targetDay >= currentDay ? targetDay - currentDay : 7 - currentDay + targetDay;
      
      nextPayment.setDate(today.getDate() + daysUntilTarget);
      if (daysUntilTarget === 0 && today.getHours() >= 9) {
        const daysToAdd = frequency === 'weekly' ? 7 : 14;
        nextPayment.setDate(nextPayment.getDate() + daysToAdd);
      }
    }
  } else {
    const targetDay = dayOfMonth === 'last' ? getLastDayOfMonth(today) : parseInt(dayOfMonth);
    nextPayment.setDate(targetDay);
    
    if (nextPayment <= today) {
      nextPayment.setMonth(nextPayment.getMonth() + 1);
      if (dayOfMonth === 'last') {
        nextPayment.setDate(getLastDayOfMonth(nextPayment));
      }
    }
  }
  
  return nextPayment.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: nextPayment.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
  });
}

// Helper function for wizard
function getLastDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

// Setup event listeners for timing controls
document.addEventListener('DOMContentLoaded', function() {
  const dayOfMonthEl = document.getElementById('day-of-month');
  if (dayOfMonthEl) {
    dayOfMonthEl.addEventListener('change', updateNextPaymentDisplay);
  }
  
  const dayOfWeekEl = document.getElementById('day-of-week');
  if (dayOfWeekEl) {
    dayOfWeekEl.addEventListener('change', updateNextPaymentDisplay);
  }
  
  const customDayEl = document.getElementById('custom-day');
  if (customDayEl) {
    customDayEl.addEventListener('input', updateNextPaymentDisplay);
  }
});